// Onboarding Types for Eduvos Performance Platform

export type OnboardingScenario = 
  | 'first-time-user'
  | 'role-change'
  | 'annual-profile-update'
  | 'campus-transfer'

export type OnboardingStep = 
  | 'welcome'
  | 'fo-confirmation'
  | 'profile-completion'
  | 'platform-tour'
  | 'manager-orientation'
  | 'complete'
  | 'preferences'
  | 'completion'

export interface FOProfileData {
  // Core identity (from F&O employee object)
  userId?: number           // Database user ID (for backend linking)
  employeeId: string        // personnel_number
  firstName: string         // first_name
  lastName: string          // last_name
  email: string             // email
  
  // Role information (from F&O hierarchy.position)
  jobTitle: string          // job_title
  jobGroup: string          // job_type
  
  // Organizational structure from F&O
  function: string          // hierarchy.function - IS in F&O
  department: string        // hierarchy.department.name - IS in F&O
  departmentCode?: string   // hierarchy.department.code
  campus: string            // EmploymentsV2.CampusName (organizational campus)
  officeLocation?: string   // Workers.OfficeLocation (physical office)
  
  // Teams - NOT in F&O, may be added later
  team1?: string            // Team 1 - NOT in F&O
  team2?: string            // Team 2 - NOT in F&O
  team3?: string            // Team 3 - NOT in F&O
  faculty?: string          // Faculty from F&O EmploymentsV2 (e.g. "Commerce & Law", "Science")
  displayName?: string      // Full display name from F&O (includes prefixes like "de Beer", "van Zyl")
  
  // Line manager (from F&O hierarchy.manager)
  lineManagerId: string     // manager.personnel_number
  lineManagerName: string   // manager.name
  lineManagerEmail: string
  lineManagerTitle?: string // manager.title
  
  // Employment details
  employmentDate: string    // hire_date
  employmentStatus: 'active' | 'probation' | 'notice' | 'inactive'  // worker_status
  contractType: string  // Raw F&O PositionTypeId (e.g. "FT Regular", "PT Temporary (IC)") or "Not specified"
  positionType?: string     // F&O PositionTypeId (FT Regular, FT Temporary, etc.)
  workerType?: string       // worker_type original value
  performanceCycle: string
  
  // Manager status (from F&O context)
  directReports: number
  isManager: boolean        // context.has_reports
  directReportsList?: {
    employeeId: string
    fullName: string
    jobTitle: string
    email: string
  }[]
  
  // Additional organizational structure
  costCenter?: string
  functionalArea?: string   // Legacy - use 'function' instead
  organizationalPath?: {    // From organizational_path
    display: string
    levels: {
      level: number
      type: string
      name: string
    }[]
  }
  reportingHierarchy: {
    level: number
    managerId: string
    managerName: string
    managerRole: string
  }[]
}

export interface UserProfileData {
  // Qualifications & Certifications
  qualifications: Qualification[]
  
  // External Career History
  careerHistory: CareerHistoryEntry[]
  
  // Professional Context
  professionalContext: ProfessionalContext
  
  // Metadata
  completionStatus: 'incomplete' | 'partial' | 'complete'
  lastUpdated: string
  nextUpdateDue?: string
}

// South African NQF Levels (HEQSF - Higher Education Qualifications Sub-Framework)
export type NQFLevel = 
  | 1  // Grade 9 / ABET Level 4
  | 2  // Grade 10 / NC(V) Level 2
  | 3  // Grade 11 / NC(V) Level 3
  | 4  // Grade 12 / National Senior Certificate / NC(V) Level 4
  | 5  // Higher Certificate (120 credits min)
  | 6  // Diploma / Advanced Certificate (240-360 credits)
  | 7  // Bachelor's Degree / Advanced Diploma / B-Tech (360-480 credits)
  | 8  // Honours Degree / Postgraduate Diploma (120 credits min)
  | 9  // Master's Degree (180 credits min)
  | 10 // Doctoral Degree / PhD (360 credits min)

// Qualification type with NQF level mapping
export type QualificationType = 
  | 'nsc'              // National Senior Certificate (NQF 4)
  | 'higher_cert'      // Higher Certificate (NQF 5)
  | 'diploma'          // Diploma (NQF 6)
  | 'adv_cert'         // Advanced Certificate (NQF 6)
  | 'adv_diploma'      // Advanced Diploma (NQF 7)
  | 'bachelors'        // Bachelor's Degree (NQF 7)
  | 'btech'            // Bachelor of Technology (NQF 7)
  | 'pgdip'            // Postgraduate Diploma (NQF 8)
  | 'honours'          // Honours Degree (NQF 8)
  | 'masters'          // Master's Degree (NQF 9)
  | 'phd'              // Doctoral Degree (NQF 10)
  | 'professional'     // Professional certification (e.g., CA, CFA, PMP)
  | 'international'    // International qualification (requires equivalency)

// Mapping of qualification types to default NQF levels
export const QUALIFICATION_NQF_MAP: Record<Exclude<QualificationType, 'professional' | 'international'>, NQFLevel> = {
  nsc: 4,
  higher_cert: 5,
  diploma: 6,
  adv_cert: 6,
  adv_diploma: 7,
  bachelors: 7,
  btech: 7,
  pgdip: 8,
  honours: 8,
  masters: 9,
  phd: 10,
}

// Qualification types that can have variable NQF levels
// Based on SAQA framework - some degrees have 3-year (NQF 7) and 4-year professional (NQF 8) variants
export const QUALIFICATION_NQF_RANGE: Partial<Record<QualificationType, { min: NQFLevel; max: NQFLevel; default: NQFLevel }>> = {
  bachelors: { min: 7, max: 8, default: 7 },  // 3-year (NQF 7) or 4-year professional like LLB, BArch, BEng (NQF 8)
  btech: { min: 7, max: 8, default: 7 },      // Standard B-Tech (NQF 7) or extended (NQF 8)
}

// Helper to get valid NQF levels for a qualification type
export const getValidNQFLevels = (type: QualificationType): NQFLevel[] => {
  const range = QUALIFICATION_NQF_RANGE[type]
  if (range) {
    const levels: NQFLevel[] = []
    for (let i = range.min; i <= range.max; i++) {
      levels.push(i as NQFLevel)
    }
    return levels
  }
  // Fixed level types
  const fixedLevel = QUALIFICATION_NQF_MAP[type as keyof typeof QUALIFICATION_NQF_MAP]
  return fixedLevel ? [fixedLevel] : []
}

// Human-readable qualification type labels
export const QUALIFICATION_LABELS: Record<QualificationType, string> = {
  nsc: 'National Senior Certificate (Grade 12)',
  higher_cert: 'Higher Certificate',
  diploma: 'Diploma',
  adv_cert: 'Advanced Certificate',
  adv_diploma: 'Advanced Diploma',
  bachelors: 'Bachelor\'s Degree',
  btech: 'Bachelor of Technology (B-Tech)',
  pgdip: 'Postgraduate Diploma',
  honours: 'Honours Degree',
  masters: 'Master\'s Degree',
  phd: 'Doctoral Degree (PhD)',
  professional: 'Professional Certification',
  international: 'International Qualification',
}

// Professional body options for certifications
export const PROFESSIONAL_BODIES = [
  { id: 'saica', name: 'SAICA - South African Institute of Chartered Accountants' },
  { id: 'hpcsa', name: 'HPCSA - Health Professions Council of South Africa' },
  { id: 'sacplan', name: 'SACPLAN - SA Council for Planners' },
  { id: 'ecsa', name: 'ECSA - Engineering Council of South Africa' },
  { id: 'lpc', name: 'LPC - Legal Practice Council' },
  { id: 'sacssp', name: 'SACSSP - SA Council for Social Service Professions' },
  { id: 'sace', name: 'SACE - South African Council for Educators' },
  { id: 'other', name: 'Other Professional Body' },
]

// CESM (Classification of Educational Subject Matter) - CHE South Africa
// Based on https://www.che.ac.za/publications/cesm-classification-educational-subject-matter
export type CESMCategory = 
  | '01' // Agriculture, Agricultural Operations and Related Sciences
  | '02' // Architecture and the Built Environment
  | '03' // Visual and Performing Arts
  | '04' // Business, Economics and Management Studies
  | '05' // Communication, Journalism and Related Studies
  | '06' // Computer and Information Sciences
  | '07' // Education
  | '08' // Engineering
  | '09' // Health Professions and Related Clinical Sciences
  | '10' // Family Ecology and Consumer Sciences
  | '11' // Languages, Linguistics and Literature
  | '12' // Law
  | '13' // Life Sciences
  | '14' // Physical Sciences
  | '15' // Mathematics and Statistics
  | '16' // Military Sciences
  | '17' // Philosophy, Religion and Theology
  | '18' // Psychology
  | '19' // Public Management and Services
  | '20' // Social Sciences
  | '99' // Other / Not Classified

export const CESM_CATEGORIES: { code: CESMCategory; name: string; examples: string[] }[] = [
  { code: '01', name: 'Agriculture, Agricultural Operations and Related Sciences', examples: ['Agricultural Sciences', 'Animal Sciences', 'Food Science', 'Horticulture'] },
  { code: '02', name: 'Architecture and the Built Environment', examples: ['Architecture', 'Urban Planning', 'Interior Design', 'Landscape Architecture'] },
  { code: '03', name: 'Visual and Performing Arts', examples: ['Fine Arts', 'Music', 'Drama', 'Film Studies', 'Graphic Design'] },
  { code: '04', name: 'Business, Economics and Management Studies', examples: ['Accounting', 'Business Management', 'Economics', 'Finance', 'Marketing', 'Human Resources', 'MBA'] },
  { code: '05', name: 'Communication, Journalism and Related Studies', examples: ['Journalism', 'Media Studies', 'Public Relations', 'Corporate Communication'] },
  { code: '06', name: 'Computer and Information Sciences', examples: ['Computer Science', 'Information Technology', 'Data Science', 'Software Engineering', 'Cybersecurity'] },
  { code: '07', name: 'Education', examples: ['Teaching', 'Educational Psychology', 'Curriculum Studies', 'Early Childhood Development', 'PGCE'] },
  { code: '08', name: 'Engineering', examples: ['Civil Engineering', 'Electrical Engineering', 'Mechanical Engineering', 'Chemical Engineering', 'Industrial Engineering'] },
  { code: '09', name: 'Health Professions and Related Clinical Sciences', examples: ['Medicine', 'Nursing', 'Pharmacy', 'Physiotherapy', 'Dentistry', 'Public Health'] },
  { code: '10', name: 'Family Ecology and Consumer Sciences', examples: ['Consumer Science', 'Nutrition', 'Food and Beverage Management', 'Hospitality'] },
  { code: '11', name: 'Languages, Linguistics and Literature', examples: ['English', 'Afrikaans', 'African Languages', 'Linguistics', 'Translation Studies'] },
  { code: '12', name: 'Law', examples: ['LLB', 'Commercial Law', 'Criminal Law', 'Constitutional Law', 'International Law'] },
  { code: '13', name: 'Life Sciences', examples: ['Biology', 'Biochemistry', 'Microbiology', 'Genetics', 'Zoology', 'Botany'] },
  { code: '14', name: 'Physical Sciences', examples: ['Physics', 'Chemistry', 'Geology', 'Environmental Science'] },
  { code: '15', name: 'Mathematics and Statistics', examples: ['Mathematics', 'Applied Mathematics', 'Statistics', 'Actuarial Science'] },
  { code: '16', name: 'Military Sciences', examples: ['Military Strategy', 'Defence Studies', 'Security Studies'] },
  { code: '17', name: 'Philosophy, Religion and Theology', examples: ['Philosophy', 'Theology', 'Religious Studies', 'Ethics'] },
  { code: '18', name: 'Psychology', examples: ['Clinical Psychology', 'Industrial Psychology', 'Educational Psychology', 'Counselling'] },
  { code: '19', name: 'Public Management and Services', examples: ['Public Administration', 'Development Studies', 'Social Work', 'Criminology'] },
  { code: '20', name: 'Social Sciences', examples: ['Sociology', 'Political Science', 'Anthropology', 'Geography', 'History'] },
  { code: '99', name: 'Other / Not Classified', examples: ['Interdisciplinary Studies', 'General Studies'] },
]

// Common South African universities for autocomplete
export const SA_UNIVERSITIES = [
  // Public Universities
  'Cape Peninsula University of Technology (CPUT)',
  'Central University of Technology (CUT)',
  'Durban University of Technology (DUT)',
  'Mangosuthu University of Technology (MUT)',
  'Nelson Mandela University (NMU)',
  'North West University (NWU)',
  'Rhodes University',
  'Sefako Makgatho University (SMU)',
  'Sol Plaatje University (SPU)',
  'Tshwane University of Technology (TUT)',
  'University of Fort Hare (UFH)',
  'University of Cape Town (UCT)',
  'University of Free State (UFS)',
  'University of Johannesburg (UJ)',
  'University of KwaZulu-Natal (UKZN)',
  'University of Limpopo (UL)',
  'University of Mpumalanga (UMP)',
  'University of Pretoria (UP)',
  'University of South Africa (UNISA)',
  'University of Stellenbosch (SU)',
  'University of Venda (UNIVEN)',
  'University of Western Cape (UWC)',
  'University of Witwatersrand (Wits)',
  'University of Zululand (UNIZULU)',
  'Vaal University of Technology (VUT)',
  'Walter Sisulu University (WSU)',
  // Private Higher Education Institutions
  'Eduvos',
  'Varsity College',
  'Regent Business School',
  'MANCOSA',
  'IIE (Independent Institute of Education)',
  'Pearson Institute',
  'Milpark Education',
  'Henley Business School Africa',
  'GIBS (Gordon Institute of Business Science)',
  'Monash South Africa',
]

// International qualification equivalency levels
export type InternationalEquivalency = 
  | 'nqf4_equivalent'  // High school equivalent
  | 'nqf5_equivalent'  // Certificate equivalent
  | 'nqf6_equivalent'  // Diploma equivalent
  | 'nqf7_equivalent'  // Degree equivalent
  | 'nqf8_equivalent'  // Honours equivalent
  | 'nqf9_equivalent'  // Masters equivalent
  | 'nqf10_equivalent' // PhD equivalent
  | 'pending_saqa'     // Awaiting SAQA evaluation
  | 'unknown'          // Unknown equivalency

export interface Qualification {
  id: string
  
  // Core qualification info
  type: QualificationType
  nqfLevel?: NQFLevel  // Auto-populated for SA qualifications, manual for international
  
  // Institution details
  institution: string
  country: 'south_africa' | 'international'
  
  // Academic details
  qualificationName?: string  // Full name of the qualification (e.g., "Bachelor of Commerce")
  cesmCategory?: CESMCategory // CESM classification code (e.g., "04" for Business)
  fieldOfStudy?: string       // Major/specialization (e.g., "Accounting")
  
  // For international qualifications
  internationalEquivalency?: InternationalEquivalency
  saqaEvaluated?: boolean     // Has SAQA evaluated this qualification?
  saqaReferenceNumber?: string
  
  // For professional certifications
  professionalBody?: string   // e.g., SAICA, HPCSA, SACE
  membershipNumber?: string
  membershipStatus?: 'active' | 'inactive' | 'pending'
  
  // Completion status
  yearCompleted?: string
  inProgress: boolean
  expectedCompletionYear?: string
  
  // Academic-specific fields for teaching staff
  creditValue?: number        // NQF credits
  thesis?: boolean            // For Masters/PhD - was a thesis component included?
  thesisTitle?: string
  
  // Verification
  verificationDocument?: string
  verified?: boolean
}

export interface CareerHistoryEntry {
  id: string
  organization: string
  organizationType?: 'education' | 'corporate' | 'government' | 'non-profit' | 'other'
  position: string
  
  // Leadership Span of Control
  directReports?: number
  extendedTeamSize?: number
  budgetResponsibility?: number
  budgetCurrency?: string
  
  // Duration
  startDate: string
  endDate?: string
  durationYears?: number
  durationMonths?: number
  
  // Achievements and Skills
  keyAchievements: string[]
  skillsDeveloped: string[]
}

export interface ProfessionalContext {
  areasOfExpertise: string[]
  languageProficiencies: LanguageProficiency[]
  geographicExperience: string[]
  industryExperience: string[]
}

export interface LanguageProficiency {
  language: string
  proficiency: 'native' | 'fluent' | 'professional' | 'conversational' | 'basic'
}

export interface OnboardingState {
  scenario: OnboardingScenario
  currentStep: OnboardingStep
  completedSteps: OnboardingStep[]
  
  // Data collected during onboarding
  foData: FOProfileData | null
  profileData: Partial<UserProfileData> | null
  
  // Validation and errors
  foDataConfirmed: boolean
  dataCorrect: boolean
  hrTicketId?: string
  
  // Preferences
  notificationPreferences?: NotificationPreferences
  
  // Tour
  tourCompleted: boolean
  tourSkipped: boolean
  
  // Progress tracking
  startedAt: string
  completedAt?: string
  estimatedTimeRemaining?: number
}

export interface NotificationPreferences {
  email: {
    reviewReminders: boolean
    feedbackRequests: boolean
    objectiveUpdates: boolean
    teamUpdates: boolean
    systemAnnouncements: boolean
  }
  inApp: {
    reviewReminders: boolean
    feedbackRequests: boolean
    objectiveUpdates: boolean
    teamUpdates: boolean
  }
  frequency: 'immediate' | 'daily-digest' | 'weekly-digest'
}

export interface OnboardingError {
  type: 'fo-user-not-found' | 'fo-data-incorrect' | 'network-error' | 'permission-denied' | 'validation-error'
  message: string
  details?: string
  requiresHRIntervention: boolean
  recoverable: boolean
}

export interface HRDataCorrectionTicket {
  ticketId: string
  employeeUserId?: number   // Database user ID (for backend linking)
  employeeId: string        // F&O personnel number
  employeeName: string
  issueType: 'profile-data' | 'direct-reports' | 'missing-manager' | 'incorrect-role' | 'incorrect-campus' | 'missing-data' | 'other'
  affectedFields: string[]
  userDescription: string
  suggestedCorrections?: Record<string, string>
  source: 'fo-confirmation' | 'manager-orientation' | 'support-widget'
  description: string
  status: 'pending' | 'in-progress' | 'resolved' | 'escalated'
  createdAt: string
  resolvedAt?: string
  assignedTo?: string
}

// Onboarding API response types
export interface OnboardingCheckResponse {
  requiresOnboarding: boolean
  scenario: OnboardingScenario
  reason: string
  foData: FOProfileData | null
  profileData: Partial<UserProfileData> | null
  estimatedDuration: number // in minutes
}

export interface ProfileCompletionStatus {
  isComplete: boolean
  completionPercentage: number
  missingFields: string[]
  lastUpdated?: string
  nextUpdateDue?: string
  overdueBy?: number // days
}

// Note: Backend API types (OnboardingStatus, OnboardingStage) are defined in @/services/api/onboardingService
// The types here (OnboardingScenario, OnboardingStep) are for the UI wizard flow
